import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import { ContainerSize } from '@freelancer/ui/container';
import { IconColor } from '@freelancer/ui/icon';
import { LinkColor, LinkHoverColor } from '@freelancer/ui/link';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import type { Observable } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-footer-deloitte',
  template: `
    <div
      class="FooterSection"
      flTrackingSection="Footer"
    >
      <fl-container
        [size]="size"
        [mobilePadding]="true"
      >
        <fl-grid>
          <fl-col [colDesktopSmall]="4">
            <div class="FooterInfo mb-small tablet:mb-large">
              <fl-link
                [link]="'/'"
                [flTrackingLabel]="'flLogo'"
              >
                <fl-logo
                  [size]="LogoSize.SMALL"
                  [backgroundColor]="BackgroundColor.DARK"
                >
                </fl-logo>
              </fl-link>
            </div>
            <div class="FooterInfo mb-small">
              <fl-icon
                class="FooterInfoIcon mr-xsmall"
                [name]="'ui-help'"
                [color]="IconColor.LIGHT"
                [useIconFont]="true"
                [fillIconFont]="true"
              >
              </fl-icon>
              <fl-link
                i18n="Footer link label"
                [link]="'/support'"
                [color]="LinkColor.LIGHT"
                [hoverColor]="LinkHoverColor.LIGHT"
                [flTrackingLabel]="'goToSupport'"
              >
                Help & Support
              </fl-link>
            </div>
          </fl-col>
          <fl-col
            [colTablet]="6"
            [colDesktopSmall]="4"
          >
            <span
              class="text-light text-body-16 FooterTitle font-bold mb-xsmall"
              i18n="Footer column title"
            >
              Terms
            </span>
            <div class="FooterNav mb-small">
              <div class="mb-xxxsmall">
                <fl-link
                  link="https://resources.deloitte.com/sites/US/system/Pages/DeloitteNet_Web_Privacy_statement.aspx"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToDeloittePrivacy'"
                >
                  Deloitte Privacy Policy
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="https://resources.deloitte.com/sites/US/About/Policies/Admin/Pages/home.aspx?US_200_223_CP"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToDeloitteConfidentiality'"
                >
                  Deloitte Confidentiality Policy
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="https://www2.deloitte.com/us/en/footerlinks1/terms-of-use.html"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToDeloitteTerms'"
                >
                  Deloitte Terms and Conditions
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/about/privacy"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToPrivacy'"
                >
                  Privacy Policy
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/about/terms"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToTerms'"
                >
                  Terms and Conditions
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/dmca"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToCopyright'"
                >
                  Copyright Policy
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/info/codeofconduct"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToCodeOfConduct'"
                >
                  Code of Conduct
                </fl-link>
              </div>
            </div>
          </fl-col>
        </fl-grid>
      </fl-container>
    </div>
    <div class="FooterSection">
      <fl-container
        [size]="size"
        [mobilePadding]="true"
      >
        <fl-grid class="FooterBordered">
          <fl-col [colTablet]="6">
            <!-- don't translate legal text -->
            <p class="text-light text-body-12">
              {{
                'Freelancer &reg; is a registered Trademark of Freelancer Technology Pty Limited (ACN 142 189 759)'
              }}
            </p>
            <p class="text-light text-body-12">
              {{ 'Copyright &copy;' }} {{ currentTime | date: 'yyyy' }}
              {{ 'Freelancer Technology Pty Limited (ACN 142 189 759)' }}
            </p>
          </fl-col>
        </fl-grid>
      </fl-container>
    </div>
  `,
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterDeloitteComponent {
  LogoSize = LogoSize;
  BackgroundColor = BackgroundColor;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  IconColor = IconColor;

  readonly currentTime: number = Date.now();
  userCount$: Observable<number>;
  projectCount$: Observable<number>;
  size: ContainerSize;

  @HostBinding('attr.role')
  @Input()
  role = 'contentinfo';
  @Input() hideFooter: boolean;

  @Input() set containerSize(value: ContainerSize) {
    this.size = value || ContainerSize.DESKTOP_LARGE;
  }
}
